import React, { useState } from 'react';
import { useTodoContext } from '../context/TodoContext';
import { useCategoryContext } from '../context/CategoryContext';
import Select from 'react-select';

const TodoForm = () => {
  const { addTodo } = useTodoContext();
  const { categories } = useCategoryContext();
  const [formData, setFormData] = useState({
    text: '',
    priority: 'medium', // Cambiato da 'media' a 'medium'
    categories: [],
    dueDate: ''
  });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { text, priority, dueDate } = formData;

  const priorityOptions = [
    { value: 'low', label: 'Bassa', color: '#10B981' }, // verde
    { value: 'medium', label: 'Media', color: '#F59E0B' }, // giallo
    { value: 'high', label: 'Alta', color: '#EF4444' } // rosso
  ];

  // Converti le categorie nel formato react-select
  const categoryOptions = categories.map(category => ({
    value: category._id,
    label: category.name,
    color: category.color
  }));

  // Stili personalizzati per react-select
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.color,
      color: getContrastYIQ(state.data.color),
      ':active': {
        backgroundColor: state.data.color,
        opacity: 0.8
      },
      cursor: 'pointer'
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.color,
      color: getContrastYIQ(state.data.color),
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      color: getContrastYIQ(state.data.color),
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: getContrastYIQ(state.data.color),
      ':hover': {
        backgroundColor: state.data.color,
        opacity: 0.8,
        color: getContrastYIQ(state.data.color),
      },
    }),
    control: (provided, state) => ({
      ...provided, 
      backgroundColor: 'var(--bg-input, white)',
      borderColor: state.isFocused ? 'var(--border-focus, #3b82f6)' : 'var(--border-input, #e2e8f0)',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
      '&:hover': {
        borderColor: 'var(--border-input-hover, #cbd5e1)'
      },
      transition: 'all 0.2s ease'
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--bg-dropdown, white)',
      zIndex: 1000
    }),
  };

  function getContrastYIQ(hexcolor) {
    if (!hexcolor || hexcolor === 'undefined') return '#ffffff';
    hexcolor = hexcolor.replace('#', '');
    
    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);
    
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? '#000000' : '#ffffff';
  }

  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(''); // Pulisci l'errore quando l'utente modifica il campo
  };

  const handleCategoryChange = selectedOptions => {
    setSelectedCategories(selectedOptions || []);
    const categoryIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFormData({ ...formData, categories: categoryIds });
    if (error) setError('');
  };

  const onSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!text.trim()) {
      setError('Per favore inserisci un\'attività');
      return;
    }

    try {
      setLoading(true);
      const todoData = {
        ...formData,
        categories: formData.categories,
        priority: formData.priority || 'medium' // Assicurati che ci sia sempre un valore di priorità
      };
      
      console.log('Creazione attività con i dati:', todoData);
      await addTodo(todoData);
      
      // Reset form
      setFormData({
        text: '',
        priority: 'medium',
        categories: [],
        dueDate: ''
      });
      setSelectedCategories([]);
    } catch (err) {
      console.error('Errore nell\'invio del form:', err);
      setError(err.response?.data?.message || 'Si è verificato un errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <form onSubmit={onSubmit}>
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 dark:bg-red-900/50 dark:text-red-300" role="alert">
            <p>{error}</p>
          </div>
        )}
        
        <div className="mb-4">
          <label htmlFor="text" className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
            Attività
          </label>
          <input
            type="text"
            name="text"
            id="text"
            value={text}
            onChange={onChange}
            className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            placeholder="Cosa c'è da fare?"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label htmlFor="priority" className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              Priorità
            </label>
            <select
              name="priority"
              id="priority"
              value={priority}
              onChange={onChange}
              className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              {priorityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="categories" className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              Categorie
            </label>
            <Select
              id="categories"
              name="categories"
              isMulti
              options={categoryOptions}
              className="react-select-container"
              classNamePrefix="react-select"
              value={selectedCategories}
              onChange={handleCategoryChange}
              placeholder="Seleziona categorie..."
              styles={customStyles}
              isDisabled={loading}
            />
          </div>
          
          <div>
            <label htmlFor="dueDate" className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              Data di Scadenza
            </label>
            <input
              type="datetime-local"
              name="dueDate"
              id="dueDate"
              value={dueDate}
              onChange={onChange}
              className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className={`
              bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              transition-all duration-200
              ${loading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {loading ? 'Aggiunta in corso...' : 'Aggiungi Attività'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TodoForm;